import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { Activity, Gauge, LayoutDashboard, Workflow } from 'lucide-react';
import {
  listDashboardRuns,
  cancelWorkflowRun,
  resumeWorkflowRun,
  abandonWorkflowRun,
  deleteWorkflowRun,
  approveWorkflowRun,
  rejectWorkflowRun,
  listCodebases,
  getHealth,
  getReadiness,
  type ReadinessCheck,
  type ReadinessResponse,
  type DashboardCounts,
  type DashboardRunResponse,
} from '@/lib/api';
import type { WorkflowRunStatus } from '@/lib/types';
import { ensureUtc } from '@/lib/format';
import { StatusSummaryBar } from '@/components/dashboard/StatusSummaryBar';
import { WorkflowRunGroup } from '@/components/dashboard/WorkflowRunGroup';
import { WorkflowRunCard } from '@/components/dashboard/WorkflowRunCard';
import { WorkflowHistoryTable } from '@/components/dashboard/WorkflowHistoryTable';
import {
  SectionedPageLayout,
  type SectionedPageLayoutItem,
} from '@/components/layout/SectionedPageLayout';
import { useDashboardSSE } from '@/hooks/useDashboardSSE';
import { useWorkflowStore } from '@/stores/workflow-store';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

/** Date range presets. "all" means no date filter. */
type DateRange = 'today' | '7d' | '30d' | 'all';
type DashboardSection = 'overview' | 'workflow-runs';

function getDateBounds(range: DateRange): { after?: string; before?: string } {
  if (range === 'all') return {};
  const now = new Date();
  const start = new Date(now);
  if (range === 'today') {
    start.setHours(0, 0, 0, 0);
  } else if (range === '7d') {
    start.setDate(start.getDate() - 7);
  } else if (range === '30d') {
    start.setDate(start.getDate() - 30);
  }
  return { after: start.toISOString() };
}

export function DashboardPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState<DashboardSection>('overview');

  // Connect to multiplexed dashboard SSE stream (all workflow events → Zustand store)
  useDashboardSSE();

  const hydrateWorkflow = useWorkflowStore(state => state.hydrateWorkflow);

  // Hydrate filter state from URL (supports bookmarkable views)
  const statusFilter = searchParams.get('status') ?? null;
  const searchQuery = searchParams.get('q') ?? '';
  const projectFilter = searchParams.get('project') ?? null;
  const dateRange: DateRange = (searchParams.get('range') as DateRange) ?? 'all';
  const page = Math.max(0, Number(searchParams.get('page') ?? '0'));
  const pageSizeParam = Number(searchParams.get('pageSize') ?? '0');
  const pageSize = PAGE_SIZE_OPTIONS.includes(pageSizeParam as (typeof PAGE_SIZE_OPTIONS)[number])
    ? pageSizeParam
    : DEFAULT_PAGE_SIZE;

  // Debounced search: type instantly in the input, but delay the server request
  const [searchInput, setSearchInput] = useState(searchQuery);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Sync searchInput when URL changes externally (e.g., back/forward)
  useEffect(() => {
    setSearchInput(searchParams.get('q') ?? '');
  }, [searchParams]);

  /** Helper to update URL params (replaces history entry to avoid back-spam). */
  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v === null || v === '' || v === '0' || v === 'all') {
              next.delete(k);
            } else {
              next.set(k, v);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setStatusFilter = useCallback(
    (status: string | null) => {
      updateParams({ status, page: null });
    },
    [updateParams]
  );
  const setProjectFilter = useCallback(
    (project: string | null) => {
      updateParams({ project, page: null });
    },
    [updateParams]
  );
  const setDateRange = useCallback(
    (range: DateRange) => {
      updateParams({ range, page: null });
    },
    [updateParams]
  );
  const updatePage = useCallback(
    (p: number) => {
      updateParams({ page: p === 0 ? null : String(p) });
    },
    [updateParams]
  );

  const setPageSize = useCallback(
    (size: number) => {
      updateParams({
        pageSize: size === DEFAULT_PAGE_SIZE ? null : String(size),
        page: null,
      });
    },
    [updateParams]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        updateParams({ q: value || null, page: null });
      }, 300);
    },
    [updateParams]
  );

  // Compute date bounds from range preset
  const dateBounds = useMemo(() => getDateBounds(dateRange), [dateRange]);

  // Server-side fetch with all filters
  const {
    data: dashboardData,
    isLoading,
    isError,
    error: fetchError,
    dataUpdatedAt,
  } = useQuery({
    queryKey: [
      'dashboardRuns',
      {
        status: statusFilter,
        codebaseId: projectFilter,
        search: searchQuery,
        dateRange,
        page,
        pageSize,
      },
    ],
    queryFn: () =>
      listDashboardRuns({
        status: (statusFilter as WorkflowRunStatus) ?? undefined,
        codebaseId: projectFilter ?? undefined,
        search: searchQuery || undefined,
        after: dateBounds.after,
        before: dateBounds.before,
        limit: pageSize,
        offset: page * pageSize,
      }),
    refetchInterval: 5_000,
  });
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState('');
  useEffect(() => {
    setLastUpdatedLabel(dataUpdatedAt > 0 ? new Date(dataUpdatedAt).toLocaleTimeString() : '');
  }, [dataUpdatedAt]);

  const runs = dashboardData?.runs ?? [];
  const total = dashboardData?.total ?? 0;
  const counts: DashboardCounts = dashboardData?.counts ?? {
    all: 0,
    running: 0,
    completed: 0,
    failed: 0,
    cancelled: 0,
    pending: 0,
    paused: 0,
  };

  // Hydrate Zustand store from REST-polled data for active runs.
  // Only sets initial state if the run isn't already tracked by SSE.
  useEffect(() => {
    for (const run of runs) {
      if (run.status === 'running' || run.status === 'pending' || run.status === 'paused') {
        hydrateWorkflow({
          runId: run.id,
          workflowName: run.workflow_name,
          status: run.status,
          dagNodes: [],
          artifacts: [],
          startedAt: new Date(ensureUtc(run.started_at)).getTime(),
          currentTool: null,
        });
      }
    }
  }, [runs, hydrateWorkflow]);

  const { data: codebases } = useQuery({
    queryKey: ['codebases'],
    queryFn: () => listCodebases(),
  });

  const { data: health } = useQuery({
    queryKey: ['health'],
    queryFn: getHealth,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  const { data: readiness } = useQuery({
    queryKey: ['readiness'],
    queryFn: getReadiness,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  // Split into active and history (from server-filtered results)
  const activeRuns = useMemo(
    () =>
      runs.filter(r => r.status === 'running' || r.status === 'pending' || r.status === 'paused'),
    [runs]
  );

  /**
   * Group active runs by parent_platform_id.
   * Multi-run groups (2+ runs from the same chat) get their own row with a header.
   * Singleton groups (1 run) are collected into a shared grid so they sit side-by-side.
   */
  const { multiRunGroups, singletonRuns } = useMemo(() => {
    const groups = new Map<
      string,
      { parentPlatformId: string | null; runs: DashboardRunResponse[] }
    >();
    for (const run of activeRuns) {
      const key = run.parent_platform_id ?? '__standalone__';
      const existing = groups.get(key);
      if (existing) {
        existing.runs.push(run);
      } else {
        groups.set(key, {
          parentPlatformId: run.parent_platform_id,
          runs: [run],
        });
      }
    }
    const multi: { parentPlatformId: string | null; runs: DashboardRunResponse[] }[] = [];
    const singles: DashboardRunResponse[] = [];
    for (const group of groups.values()) {
      if (group.runs.length > 1) {
        multi.push(group);
      } else {
        singles.push(group.runs[0]);
      }
    }
    return { multiRunGroups: multi, singletonRuns: singles };
  }, [activeRuns]);

  const historyRuns = useMemo(
    () =>
      runs.filter(
        r => r.status === 'completed' || r.status === 'failed' || r.status === 'cancelled'
      ),
    [runs]
  );

  const [actionError, setActionError] = useState<string | null>(null);

  async function runAction(
    action: (runId: string) => Promise<unknown>,
    runId: string,
    fallbackMessage: string
  ): Promise<void> {
    try {
      setActionError(null);
      await action(runId);
      void queryClient.invalidateQueries({ queryKey: ['dashboardRuns'] });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : fallbackMessage);
    }
  }

  const handleCancel = (runId: string): Promise<void> =>
    runAction(cancelWorkflowRun, runId, 'Failed to cancel workflow');
  const handleResume = (runId: string): Promise<void> =>
    runAction(resumeWorkflowRun, runId, 'Failed to resume workflow');
  const handleAbandon = (runId: string): Promise<void> =>
    runAction(abandonWorkflowRun, runId, 'Failed to abandon workflow');
  const handleDelete = (runId: string): Promise<void> =>
    runAction(deleteWorkflowRun, runId, 'Failed to delete workflow run');
  const handleApprove = (runId: string): Promise<void> =>
    runAction(approveWorkflowRun, runId, 'Failed to approve workflow');
  const handleReject = (runId: string): Promise<void> =>
    runAction(rejectWorkflowRun, runId, 'Failed to reject workflow');

  const totalPages = Math.ceil(total / pageSize);
  const hasMore = page + 1 < totalPages;

  const sectionItems: readonly SectionedPageLayoutItem<DashboardSection>[] = [
    {
      id: 'overview',
      label: 'Overview',
      icon: LayoutDashboard,
      description: 'Operational summary',
    },
    {
      id: 'workflow-runs',
      label: 'Workflow Runs',
      icon: Workflow,
      description: 'Queue, actions, history',
      badge:
        counts.all > 0 ? (
          <span className="rounded-full bg-surface-elevated px-2 py-0.5 text-[11px] text-text-secondary">
            {String(counts.all)}
          </span>
        ) : undefined,
    },
  ];

  const workflowRunsContent = (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-text-primary">Workflow Runs</h2>
          <p className="mt-1 text-xs text-text-tertiary">
            Filter runs, inspect active executions, and manage workflow history.
          </p>
        </div>
      </div>

      {/* Status Summary Bar receives real server counts and owns run filters. */}
      <StatusSummaryBar
        counts={counts}
        activeFilter={statusFilter}
        onFilterChange={setStatusFilter}
        searchQuery={searchInput}
        onSearchChange={handleSearchChange}
        projectFilter={projectFilter}
        onProjectFilterChange={setProjectFilter}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        codebases={codebases}
        health={health}
      />

      {actionError && (
        <div className="rounded-md border border-error/30 bg-error/5 px-4 py-3 text-sm text-error">
          {actionError}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <span className="text-sm text-text-tertiary">Loading...</span>
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <p className="text-sm text-error">
            Failed to load workflow runs
            {fetchError instanceof Error ? `: ${fetchError.message}` : ''}
          </p>
        </div>
      ) : runs.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <Workflow className="size-10 text-text-tertiary" />
          <p className="text-sm text-text-tertiary">No workflow runs found</p>
        </div>
      ) : (
        <>
          {activeRuns.length > 0 && (
            <section>
              <h3 className="mb-3 text-sm font-semibold text-text-secondary">Active Workflows</h3>
              <div className="space-y-6">
                {singletonRuns.length > 0 && (
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {singletonRuns.map(run => (
                      <WorkflowRunCard
                        key={run.id}
                        run={run}
                        isDocker={health?.is_docker}
                        onCancel={handleCancel}
                        onResume={handleResume}
                        onAbandon={handleAbandon}
                        onDelete={handleDelete}
                        onApprove={handleApprove}
                        onReject={handleReject}
                      />
                    ))}
                  </div>
                )}
                {multiRunGroups.map(group => (
                  <WorkflowRunGroup
                    key={group.parentPlatformId ?? 'standalone'}
                    parentPlatformId={group.parentPlatformId}
                    runs={group.runs}
                    isDocker={health?.is_docker}
                    onCancel={handleCancel}
                    onResume={handleResume}
                    onAbandon={handleAbandon}
                    onDelete={handleDelete}
                    onApprove={handleApprove}
                    onReject={handleReject}
                  />
                ))}
              </div>
            </section>
          )}

          {historyRuns.length > 0 && (
            <section>
              <h3 className="mb-3 text-sm font-semibold text-text-secondary">History</h3>
              <WorkflowHistoryTable runs={historyRuns} onDelete={handleDelete} />
            </section>
          )}

          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-3">
              <span className="text-xs text-text-tertiary">
                Showing {String(page * pageSize + 1)}-
                {String(Math.min((page + 1) * pageSize, total))} of {String(total)} runs
              </span>
              <select
                value={pageSize}
                onChange={(e): void => {
                  setPageSize(Number(e.target.value));
                }}
                className="rounded-md border border-border bg-surface-elevated px-2 py-1 text-xs text-text-primary focus:border-primary focus:outline-none"
              >
                {PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>
                    {String(size)} per page
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(): void => {
                  updatePage(page - 1);
                }}
                disabled={page === 0}
                className="rounded-md border border-border bg-surface-elevated px-3 py-1 text-xs text-text-secondary transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs text-text-tertiary">
                Page {String(page + 1)} of {String(Math.max(1, totalPages))}
              </span>
              <button
                onClick={(): void => {
                  updatePage(page + 1);
                }}
                disabled={!hasMore}
                className="rounded-md border border-border bg-surface-elevated px-3 py-1 text-xs text-text-secondary transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const overviewContent = (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-semibold text-text-primary">Overview</h2>
        <p className="mt-1 text-xs text-text-tertiary">
          Current platform health, workflow capacity, and recent run activity.
        </p>
      </div>

      <section className="overflow-hidden rounded-lg border border-border bg-surface">
        <div className="grid divide-border md:grid-cols-4 md:divide-x">
          <div className="px-4 py-3">
            <div className="flex items-center gap-1.5">
              <span className="size-1.5 rounded-full bg-accent-bright shadow-[0_0_6px_var(--accent-bright)]" />
              <span className="font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-tertiary">
                Active
              </span>
            </div>
            <div className="mt-1 text-2xl font-semibold leading-none tracking-tight text-text-primary">
              {String(counts.running + counts.pending + counts.paused)}
            </div>
          </div>
          <div className="px-4 py-3">
            <div className="flex items-center gap-1.5">
              <span className="size-1.5 rounded-full bg-error" />
              <span className="font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-tertiary">
                Failed
              </span>
            </div>
            <div className="mt-1 text-2xl font-semibold leading-none tracking-tight text-text-primary">
              {String(counts.failed)}
            </div>
          </div>
          <div className="px-4 py-3">
            <div className="flex items-center gap-1.5">
              <span className="size-1.5 rounded-full bg-success" />
              <span className="font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-tertiary">
                Completed
              </span>
            </div>
            <div className="mt-1 text-2xl font-semibold leading-none tracking-tight text-text-primary">
              {String(counts.completed)}
            </div>
          </div>
          <div className="px-4 py-3">
            <div className="flex items-center gap-1.5">
              <span className="size-1.5 rounded-full bg-flame" />
              <span className="font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-tertiary">
                Capacity
              </span>
            </div>
            <div className="mt-1 font-mono text-2xl font-semibold leading-none tracking-tight text-text-primary">
              {health
                ? `${String(health.concurrency.active)}/${String(health.concurrency.maxConcurrent)}`
                : '—'}
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="mb-2 flex items-center gap-2">
          <Activity className="size-3.5 text-accent-bright" />
          <h3 className="font-mono text-[11px] font-bold uppercase tracking-widest text-text-secondary">
            Recent Workflow Activity
          </h3>
        </div>
        {runs.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface px-4 py-6 text-center text-sm text-text-tertiary">
            No workflow runs match the current filters.
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border bg-surface">
            <div className="divide-y divide-border">
              {runs.slice(0, 6).map(run => (
                <div
                  key={run.id}
                  className="grid items-center gap-2 px-4 py-3 text-sm md:grid-cols-[1fr_auto_auto]"
                >
                  <div className="min-w-0">
                    <div className="truncate font-mono text-[13px] font-medium text-text-primary">
                      {run.workflow_name}
                    </div>
                    <div className="truncate font-mono text-[11px] text-text-tertiary">
                      {run.codebase_name ?? 'No codebase'} · {run.id}
                    </div>
                  </div>
                  <span className="font-mono text-[11px] uppercase tracking-wide text-text-secondary">
                    {run.status}
                  </span>
                  <span className="font-mono text-[11px] text-text-tertiary">
                    {new Date(ensureUtc(run.started_at)).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <section>
        <div className="mb-2 flex items-center gap-2">
          <Gauge className="size-3.5 text-success" />
          <h3 className="font-mono text-[11px] font-bold uppercase tracking-widest text-text-secondary">
            System Status
          </h3>
        </div>
        <div className="overflow-hidden rounded-lg border border-border bg-surface">
          <div className="divide-y divide-border">
            <StatusRow
              label="Liveness"
              value={health?.status ?? 'Loading'}
              tone={health ? 'ok' : 'pending'}
            />
            <StatusRow
              label="Readiness"
              value={readiness ? (readiness.status === 'ready' ? 'ready' : 'not_ready') : 'Loading'}
              tone={readiness ? (readiness.status === 'ready' ? 'ok' : 'error') : 'pending'}
            />
            {readiness &&
              READINESS_CHECK_ORDER.map(key => (
                <ReadinessRow
                  key={key}
                  label={READINESS_LABELS[key]}
                  check={readiness.checks[key]}
                />
              ))}
            <div className="flex items-center justify-between px-4 py-3 text-sm">
              <span className="text-text-tertiary">Adapter</span>
              <span className="font-mono text-text-primary">{health?.adapter ?? '—'}</span>
            </div>
            <div className="flex items-center justify-between px-4 py-3 text-sm">
              <span className="text-text-tertiary">Queued workflows</span>
              <span className="font-mono text-text-primary">
                {health ? String(health.concurrency.queuedTotal) : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between px-4 py-3 text-sm">
              <span className="text-text-tertiary">Running workflows</span>
              <span className="font-mono text-text-primary">
                {health ? String(health.runningWorkflows) : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between px-4 py-3 text-sm">
              <span className="text-text-tertiary">Docker runtime</span>
              <span className="font-mono text-text-primary">
                {health ? (health.is_docker ? 'Enabled' : 'Disabled') : '—'}
              </span>
            </div>
            {health?.version && (
              <div className="flex items-center justify-between px-4 py-3 text-sm">
                <span className="text-text-tertiary">Version</span>
                <span className="font-mono text-text-primary">{health.version}</span>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );

  return (
    <SectionedPageLayout
      title="Metis Overview"
      description="Platform operations at a glance"
      items={sectionItems}
      activeId={activeSection}
      onActiveChange={setActiveSection}
      headerActions={
        dataUpdatedAt > 0 ? (
          <span suppressHydrationWarning className="text-xs text-text-tertiary">
            Last updated {lastUpdatedLabel}
          </span>
        ) : null
      }
    >
      {activeSection === 'overview' && overviewContent}
      {activeSection === 'workflow-runs' && workflowRunsContent}
    </SectionedPageLayout>
  );
}

type ReadinessCheckKey = keyof ReadinessResponse['checks'];

const READINESS_CHECK_ORDER: readonly ReadinessCheckKey[] = [
  'databaseMigrations',
  'secretStore',
  'eventBus',
  'objectStore',
  'authConfig',
  'githubAppConfig',
  'githubAppReachability',
  'workflowDispatch',
  'collab',
];

const READINESS_LABELS: Record<ReadinessCheckKey, string> = {
  databaseMigrations: 'Database',
  secretStore: 'Secret store',
  eventBus: 'Event bus',
  objectStore: 'Object store',
  authConfig: 'Auth config',
  githubAppConfig: 'GitHub App config',
  githubAppReachability: 'GitHub App reachability',
  workflowDispatch: 'Workflow dispatch',
  collab: 'Collab service',
};

function statusToneClass(tone: 'ok' | 'error' | 'pending'): string {
  if (tone === 'ok') return 'text-success';
  if (tone === 'error') return 'text-error';
  return 'text-text-tertiary';
}

function StatusRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'ok' | 'error' | 'pending';
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between px-4 py-3 text-sm">
      <span className="text-text-tertiary">{label}</span>
      <span className={`font-mono ${statusToneClass(tone)}`}>{value}</span>
    </div>
  );
}

function ReadinessRow({
  label,
  check,
}: {
  label: string;
  check: ReadinessCheck;
}): React.ReactElement {
  const tone = check.status === 'ok' ? 'ok' : 'error';
  return (
    <div className="flex items-start justify-between gap-3 px-4 py-3 text-sm">
      <span className="text-text-tertiary">{label}</span>
      <span className={`text-right font-mono ${statusToneClass(tone)}`} title={check.message}>
        {check.status === 'ok' ? 'ok' : 'error'}
        {check.status === 'error' && (
          <span className="ml-2 text-xs text-text-tertiary">{check.message}</span>
        )}
      </span>
    </div>
  );
}
